import { useCallback } from 'react'
import { useMutation } from '@apollo/client'
import { useSession } from 'packages/@slerp/accounts'
import {
  ARCHIVE_LOYALTY_MULTIPLIER,
  SET_LOYALTY_MULTIPLIER_ACTIVE,
  UNARCHIVE_LOYALTY_MULTIPLIER
} from './queries'
import {
  ArchiveLoyaltyMultiplierData,
  ArchiveLoyaltyMultiplierVars,
  LoyaltyMultiplierActionResult,
  SetLoyaltyMultiplierActiveData,
  SetLoyaltyMultiplierActiveVars,
  UnarchiveLoyaltyMultiplierData,
  UnarchiveLoyaltyMultiplierVars
} from './types'

interface UseMultiplierActionsReturn {
  setActive: (
    multiplierId: string,
    active: boolean
  ) => Promise<LoyaltyMultiplierActionResult>
  archive: (multiplierId: string) => Promise<LoyaltyMultiplierActionResult>
  restore: (multiplierId: string) => Promise<LoyaltyMultiplierActionResult>
}

// Callers use the resolved value, so skip the hook's result state and its re-renders
const OPTIONS = { fetchPolicy: 'no-cache', ignoreResults: true } as const

/** Pause/resume, archive and restore for the session Merchant. Resolves to the multiplier or rejects */
export const useMultiplierActions = (): UseMultiplierActionsReturn => {
  const { merchant } = useSession()
  const merchantId = merchant?.id || ''

  const [setActiveMutation] = useMutation<
    SetLoyaltyMultiplierActiveData,
    SetLoyaltyMultiplierActiveVars
  >(SET_LOYALTY_MULTIPLIER_ACTIVE, OPTIONS)
  const [archiveMutation] = useMutation<
    ArchiveLoyaltyMultiplierData,
    ArchiveLoyaltyMultiplierVars
  >(ARCHIVE_LOYALTY_MULTIPLIER, OPTIONS)
  const [restoreMutation] = useMutation<
    UnarchiveLoyaltyMultiplierData,
    UnarchiveLoyaltyMultiplierVars
  >(UNARCHIVE_LOYALTY_MULTIPLIER, OPTIONS)

  const setActive = useCallback(
    async (multiplierId: string, active: boolean) => {
      const { data } = await setActiveMutation({
        variables: { merchantId, multiplierId, active }
      })
      if (!data) throw new Error('No data returned')
      return data.setLoyaltyMultiplierActive
    },
    [setActiveMutation, merchantId]
  )

  const archive = useCallback(
    async (multiplierId: string) => {
      const { data } = await archiveMutation({
        variables: { merchantId, multiplierId }
      })
      if (!data) throw new Error('No data returned')
      return data.archiveLoyaltyMultiplier
    },
    [archiveMutation, merchantId]
  )

  const restore = useCallback(
    async (multiplierId: string) => {
      const { data } = await restoreMutation({
        variables: { merchantId, multiplierId }
      })
      if (!data) throw new Error('No data returned')
      return data.unarchiveLoyaltyMultiplier
    },
    [restoreMutation, merchantId]
  )

  return { setActive, archive, restore }
}
